<script setup>
import {defineProps, computed, onMounted, reactive, ref} from 'vue';
import {APIService} from '@/core/util/services/api.service';
import OxdLabel from '@ohrm/oxd/core/components/Label/Label.vue';
import States from '../../constants/States';
import TrainingTypes from '../../constants/TrainingTypes';
import DeliveryMethods from '../../constants/DeliveryMethods.ts';

const props = defineProps({
  training: {type: Object},
  departments: {type: Array},
});

const trainers = reactive({
  names: '',
  emails: '',
});

const subUnits = ref([]);
const http = new APIService(window.appGlobal.baseUrl, 'api/');

// Methods
const getDepartmentSubUnits = async departments => {
  const departmentIds = departments.map(department => department.id);
  await http
    .request({
      method: 'GET',
      url: `api/training/departments/sub-units`,
      params: {departmentIds},
    })
    .then(response => {
      subUnits.value = response.data;
    });
};

const openFile = path => {
  window.open(`${window.appGlobal.publicPath}/${path}`, '_blank');
};

onMounted(() => {
  if (props.training.training_type == TrainingTypes[0].id) {
    const names = props.training?.trainer.map(item => item.label).join(', ');
    trainers.names = names;
  } else {
    const names = props.training?.trainer
      .map(item => item.name)
      .filter(item => item != '')
      .join(', ');
    const emails = props.training?.trainer
      .map(item => item.email)
      .filter(item => item != '')
      .join(', ');
    trainers.names = names;
    trainers.emails = emails;
  }

  getDepartmentSubUnits(props.departments);
});

const status = computed(() => {
  return States.find(item => item.id === props.training.status)?.name;
});

const departmentsNames = computed(() => {
  return props.departments
    .filter(department => props.training.department.includes(department.id))
    .map(department => department.label)
    .join(', ');
});

const subunitsNames = computed(() => {
  return subUnits.value
    .filter(item => props.training.subunit.includes(item.id))
    .map(item => item.label)
    .join(', ');
});

const participantsNames = computed(() => {
  if (props.training.participants == 'All') {
    return props.training.participants;
  }
  return props.training.participantsDetails
    .filter(participant => props.training.participants.includes(participant.id))
    .map(item => item.name)
    .join(', ');
});

const trainingType = computed(() => {
  return TrainingTypes.find(item => item.id == props.training.training_type)
    ?.name;
});

const deliveryMethod = computed(() => {
  return DeliveryMethods.find(
    delivery => delivery.id == props.training.delivery_method,
  )?.name;
});
</script>
<template>
  <div class="orangehrm-background-container">
    <oxd-form>
      <oxd-form-row>
        <oxd-grid :cols="3" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              :value="`TRN-${training.id}`"
              label="ID"
              :disabled="true"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              :value="training.title"
              label="Title"
              :disabled="true"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field :value="status" label="Status" :disabled="true" />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-form-row>
        <oxd-input-field
          :value="training.description"
          type="textarea"
          label="Description"
          :disabled="true"
        />
      </oxd-form-row>
      <oxd-form-row>
        <oxd-grid :cols="4" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              :value="trainingType"
              label="Training Type"
              :disabled="true"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              :value="training.duration"
              label="Duration"
              :disabled="true"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <date-input
              :value="training.start_date"
              label="Start Date"
              :disabled="true"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <date-input
              :value="training.due_date"
              label="Due Date"
              :disabled="true"
            />
          </oxd-grid-item>
        </oxd-grid>
        <oxd-grid
          v-if="props.training.training_type != TrainingTypes[0].id"
          :cols="2"
          class="orangehrm-full-width-grid"
        >
          <oxd-grid-item>
            <oxd-input-field
              :value="trainers.emails"
              label="Trainer(s) Email(s)"
              :disabled="true"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              :value="trainers.names"
              label="Trainer(s) Name(s)"
              :disabled="true"
            />
          </oxd-grid-item>
        </oxd-grid>
        <oxd-grid v-else :cols="2" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              :value="trainers.names"
              label="Trainer(s)"
              :disabled="true"
            />
          </oxd-grid-item>
        </oxd-grid>
        <oxd-grid :cols="2" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              :value="departmentsNames"
              label="Department(s)"
              :disabled="true"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              :value="subunitsNames"
              label="Sub Unit(s)"
              :disabled="true"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              :value="participantsNames"
              label="Training Assignees"
              :disabled="true"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              :value="training.onboardingAssignees"
              label="Onboarding Assignees"
              :disabled="true"
            />
          </oxd-grid-item>
        </oxd-grid>
        <oxd-grid :cols="4" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              :value="training.priority ? 'Yes' : 'No'"
              label="Priority"
              :disabled="true"
            />
          </oxd-grid-item>
          <oxd-grid-item class="--span-column-3">
            <!--  -->
          </oxd-grid-item>
        </oxd-grid>
        <oxd-grid :cols="4" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              :value="deliveryMethod"
              label="Delivery Method"
              :disabled="true"
            />
          </oxd-grid-item>
          <oxd-grid-item class="--span-column-3">
            <oxd-input-field
              :value="training.detail"
              label="Detail"
              placeholder="Detail"
              :disabled="true"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <oxd-form-row>
        <OxdLabel label="Details" />
        <br />
        <div class="oxd-input" v-html="training.details"></div>
      </oxd-form-row>
      <oxd-form-row>
        <OxdLabel label="Training Schedule" style="margin-bottom: 0.4rem;" />
        <table class="w-100" style="table-layout: fixed;">
          <tbody>
            <tr
              v-for="(schedule, index) in training.training_schedule"
              :key="index"
              class="flex"
            >
              <td class="w-20">
                <oxd-input-field
                  :value="schedule.session"
                  label="Session"
                  :disabled="true"
                />
              </td>
              <td class="w-20">
                <div class="flex gap-3">
                  <oxd-input-field
                    :value="schedule.date"
                    label="Date"
                    :disabled="true"
                  />
                  <oxd-input-field
                    :value="schedule.hour"
                    label="Hour"
                    :disabled="true"
                  />
                </div>
              </td>
              <td>
                <div class="flex gap-3 flex-column">
                  <OxdLabel label="Activies" />
                  <div class="oxd-input" v-html="schedule.activity"></div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </oxd-form-row>
      <oxd-form-row>
        <oxd-grid :cols="5" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <oxd-input-field
              :value="training.assesment_required ? 'Yes' : 'No'"
              label="Assesment Required"
              :disabled="true"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <date-input
              :value="training.assessment_due_date"
              label="Assessment Due Date"
              :disabled="true"
            />
          </oxd-grid-item>
          <oxd-grid-item class="--span-column-3">
            <oxd-input-field
              :value="training.evaluationMethod"
              label="Evaluation Method"
              placeholder="Evaluation Method"
              :disabled="true"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>
      <br />
      <oxd-form-row>
        <oxd-text tag="h6">
          Files
        </oxd-text>
        <div
          v-for="file in training.files"
          :key="file"
          class="flex gap-3 items-center"
        >
          <oxd-icon-button name="download" @click="openFile(file)" />
          <OxdLabel :label="file" />
        </div>
      </oxd-form-row>

      <br />

      <oxd-form-row>
        <oxd-input-field
          :value="training.comments"
          type="textarea"
          label="Comments"
          :disabled="true"
        />
      </oxd-form-row>
    </oxd-form>
  </div>
</template>
<style scoped>
.w-100 {
  width: 100%;
}

.w-20 {
  width: 20%;
}

.flex {
  display: flex;
}

.flex-column {
  flex-direction: column;
}

.items-center {
  align-items: center;
}

.flex {
  display: flex;
}

.gap-3 {
  gap: 3px;
}
.oxd-input {
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
  font-family: 'Nunito Sans';
  color: #64728c;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  border-radius: 0.65rem;
  padding: 0.675rem 0.5rem;
  width: 100%;
  min-height: 45px;
}

.custom-checkbox-direction {
  flex-direction: row-reverse !important;
  display: flex !important;
  justify-content: flex-end !important;
}
</style>
